import app from 'flarum/forum/app';
const TERM = '.ChampisGlossaryTerm';
const POPOVER_ID = 'ChampisGlossaryPopover';
const MARGIN = 8;
let find = () => undefined;
let popover = null;
let current = null;
let showTimer;
let hideTimer;
let lastPointer = {
  type: '',
  time: 0
};

/**
 * The definition of an underlined glossary term (annotateGlossaryTerms.ts),
 * in a bubble below it: on hover with a mouse, on a tap on a touch screen
 * (a second one, or one elsewhere, closes it), on focus with the keyboard
 * (Escape closes it). Its "Voir dans le glossaire" leads to the term's
 * discussion.
 *
 * Plain DOM listened to on the document: the terms are inside a post's
 * own HTML (m.trust), never Mithril's.
 */
export default function installGlossaryPopover(findTerm) {
  find = findTerm;
  document.addEventListener('pointerover', e => {
    if (e.pointerType !== 'mouse') return;
    if (inPopover(e.target)) {
      window.clearTimeout(hideTimer);
      return;
    }
    const term = termOf(e.target);
    if (!term) return;
    window.clearTimeout(hideTimer);
    window.clearTimeout(showTimer);
    if (term !== current) showTimer = window.setTimeout(() => show(term), 200);
  });
  document.addEventListener('pointerout', e => {
    if (e.pointerType !== 'mouse') return;
    const from = termOf(e.target) ?? (inPopover(e.target) ? popover : null);
    const to = e.relatedTarget;
    if (!from || inPopover(to) || to instanceof Node && (from.contains(to) || current?.contains(to))) return;
    window.clearTimeout(showTimer);
    hideTimer = window.setTimeout(hide, 300);
  });
  document.addEventListener('pointerdown', e => {
    lastPointer = {
      type: e.pointerType,
      time: Date.now()
    };
  }, true);
  document.addEventListener('click', e => {
    if (inPopover(e.target)) return;
    const term = termOf(e.target);
    if (!term) {
      hide();
    } else if (term !== current) {
      show(term);
    } else if (lastPointer.type !== 'mouse') {
      // A second tap. A mouse's click on a term it hovers keeps it open.
      hide();
    }
  });
  document.addEventListener('focusin', e => {
    const term = termOf(e.target);

    // A tap focuses the term too: the click shows it.
    if (term && term !== current && Date.now() - lastPointer.time > 500) show(term);
  });
  document.addEventListener('focusout', e => {
    const to = e.relatedTarget;
    if (!current || !(e.target === current || inPopover(e.target))) return;
    if (to === current || inPopover(to)) return;
    // Focus leaving with a click: the click decides.
    if (Date.now() - lastPointer.time < 500) return;
    hide();
  });
  document.addEventListener('keydown', e => {
    const term = termOf(e.target);
    if (term && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      if (term === current) hide();else show(term);
    } else if (e.key === 'Escape' && current) {
      const focused = current;
      const inside = popover?.contains(document.activeElement);
      hide();
      if (inside) focused.focus();
    }
  });
  window.addEventListener('resize', hide);
  window.addEventListener('popstate', hide);
}
function termOf(target) {
  return target instanceof Element ? target.closest(TERM) : null;
}
function inPopover(target) {
  return !!popover && target instanceof Node && popover.contains(target);
}
function show(term) {
  const entry = find(term.dataset.glossaryTerm || '');
  hide();
  if (!entry) return;
  popover = build(entry);
  document.body.append(popover);
  current = term;
  current.setAttribute('aria-describedby', POPOVER_ID);
  current.setAttribute('aria-expanded', 'true');
  place(popover, term);
}
function hide() {
  window.clearTimeout(showTimer);
  window.clearTimeout(hideTimer);
  popover?.remove();
  popover = null;
  current?.removeAttribute('aria-describedby');
  current?.removeAttribute('aria-expanded');
  current = null;
}
function build(entry) {
  const element = document.createElement('div');
  element.id = POPOVER_ID;
  element.className = 'ChampisGlossaryPopover';
  element.setAttribute('role', 'tooltip');
  const term = document.createElement('div');
  term.className = 'ChampisGlossaryPopover-term';
  term.textContent = entry.term;
  const definition = document.createElement('p');
  definition.className = 'ChampisGlossaryPopover-definition';
  definition.textContent = entry.definition;
  const link = document.createElement('a');
  link.className = 'ChampisGlossaryPopover-link';
  // The discussion's slug is its route id, "1-hymenophore".
  link.href = app.route('discussion', {
    id: entry.slug || entry.id
  });
  link.textContent = app.translator.trans('champis-others.forum.glossary_terms.link', {}, true);

  // Within the forum, like its own links - a new tab with a modifier.
  link.addEventListener('click', e => {
    if (e.button !== 0 || e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    hide();
    m.route.set(link.getAttribute('href'));
  });
  element.append(term, definition, link);
  return element;
}

// Below the term (its first line, if it wraps), or above it when there's
// more room there; within the window's width.
function place(element, term) {
  const rect = term.getClientRects()[0] ?? term.getBoundingClientRect();
  const width = element.offsetWidth;
  const height = element.offsetHeight;
  const viewportWidth = document.documentElement.clientWidth;
  let top = rect.bottom + MARGIN;
  if (top + height > window.innerHeight && rect.top - MARGIN - height > 0) {
    top = rect.top - MARGIN - height;
  }
  const left = Math.max(MARGIN, Math.min(rect.left, viewportWidth - width - MARGIN));
  element.style.top = `${top + window.scrollY}px`;
  element.style.left = `${left + window.scrollX}px`;
}